// An action that asks for a few words first, answered in a popover anchored to
// the button that asked, so the confirm lands under the pointer rather than
// across the screen.

import React, { useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { TextArea } from '../components/ui/Field';
import Popover from '../components/ui/Popover';
import Alert from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';

// React 17: no useId. A module counter is enough, nothing here is server rendered.
let seq = 0;

export interface ReasonPopoverProps {
  /** The word on the button that opens it. */
  triggerLabel: string;
  triggerClassName: string;
  /** Hover text on the trigger, saying what happens. Hung on a wrapper span. */
  tooltip?: string;
  title: string;
  fieldLabel: string;
  required: boolean;
  maxLength?: number;
  placeholder?: string;
  confirmLabel: string;
  confirmTone: 'primary' | 'danger';
  /** Shown under the field, one line. */
  hint?: string;
  disabled?: boolean;
  busy?: boolean;
  /** Resolve true to close; false keeps the popover open (the page toasted why). */
  onConfirm: (text: string) => Promise<boolean>;
}

const CONFIRM_TONE = {
  primary: buttonClass('filled', 'sm'),
  danger: buttonClass('danger', 'sm'),
};

const ReasonPopover: React.FC<ReasonPopoverProps> = ({
  triggerLabel,
  triggerClassName,
  tooltip,
  title,
  fieldLabel,
  required,
  maxLength = 1000,
  placeholder,
  confirmLabel,
  confirmTone,
  hint,
  disabled,
  busy,
  onConfirm,
}) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const idRef = useRef<string>('');
  if (!idRef.current) idRef.current = `reason-popover-${++seq}`;
  const id = idRef.current;

  const close = () => {
    setOpen(false);
    setError(null);
  };

  const submit = async () => {
    const trimmed = text.trim();
    if (required && !trimmed) {
      setError('Add a few words so the next person knows why.');
      fieldRef.current?.focus();
      return;
    }
    const ok = await onConfirm(trimmed);
    if (ok) {
      setText('');
      close();
    }
  };

  return (
    <div className="relative inline-flex">
      <Tooltip label={tooltip || ''}>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => (open ? close() : setOpen(true))}
          disabled={disabled || busy}
          aria-expanded={open}
          aria-controls={open ? id : undefined}
          className={triggerClassName}
        >
          {triggerLabel}
        </button>
      </Tooltip>
      <Popover open={open} onClose={close} anchor={triggerRef} id={id} title={title} hideClose>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="space-y-2"
        >
          <label className="block">
            <span className="mb-1 block text-xs text-on-surface-variant">{fieldLabel}</span>
            <TextArea
              ref={fieldRef}
              value={text}
              rows={3}
              maxLength={maxLength}
              placeholder={placeholder}
              onChange={(e) => {
                setText(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  submit();
                }
              }}
              error={!!error}
              aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
            />
          </label>
          {(error || hint) && (
            <FadeIn key={error ? 'error' : 'hint'}>
              {error ? (
                <div id={`${id}-error`}>
                  <Alert tone="error" variant="text" live={false} className="text-xs">{error}</Alert>
                </div>
              ) : (
                <p id={`${id}-hint`} className="text-xs text-on-surface-variant">
                  {hint}
                </p>
              )}
            </FadeIn>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => close()}
              className={buttonClass('quiet', 'sm')}
            >
              Never mind
            </button>
            <button
              type="submit"
              disabled={busy}
              className={CONFIRM_TONE[confirmTone]}
            >
              {busy ? 'Working…' : confirmLabel}
            </button>
          </div>
        </form>
      </Popover>
    </div>
  );
};

export default ReasonPopover;
